<script lang="ts">
  import MetalFrame from '../hud/MetalFrame.svelte';
  import LobbyChat from './LobbyChat.svelte';
  import ShipArt from '../ShipArt.svelte';
  import { socketState } from '../socket-state.svelte';
  import { sharedAssets } from '../../assets/asset-store';
  import { atlasSignal } from '../../assets/atlas-signal.svelte';
  import { wornSkin } from '../hud/worn-skin';
  import { LOBBY_UI } from '../../game/constants/lobby';
  import { UNNAMED_NAME } from '../../game/constants/world';
  import type { ChatLine } from '../../game/world';
  import {
    PLACEHOLDER_DIFFICULTY_TIERS,
    canCancelStart,
    canSetDifficulty,
    canSpectate,
    canStart,
    canTakeSlot,
    startSeconds,
    difficultyOf,
    humanCount,
    isHost,
    isSpectating,
    joinLink,
    occupantPlayer,
    teamSlots,
    type LobbySlot,
    type LobbyView,
    type PlaceholderDifficulty,
    type SeatShip,
  } from '../../lobby/lobby-state';

  interface Props {
    lobby: LobbyView;
    selfId: number;
    ontake: (slotId: number) => void;
    onspectate: () => void;
    ondifficulty: (team: number, difficulty: PlaceholderDifficulty) => void;
    onchat: (text: string) => void;
    chat: readonly ChatLine[];
    onstart: () => void;
    oncancelstart: () => void;
    onleave: () => void;
  }

  const {
    lobby,
    selfId,
    chat,
    ontake,
    onspectate,
    ondifficulty,
    onchat,
    onstart,
    oncancelstart,
    onleave,
  }: Props = $props();

  const watching = $derived(isSpectating(lobby, selfId));
  const mayWatch = $derived(canSpectate(lobby, selfId));
  const taken = $derived(humanCount(lobby));
  const mayTune = $derived(canSetDifficulty(lobby, selfId));

  let copied = $state(false);

  function copyLink(): void {
    const link = joinLink(window.location.origin, lobby.joinCode);
    void navigator.clipboard.writeText(link).then(
      () => {
        copied = true;
      },
      () => {
        copied = false;
      },
    );
  }

  /** A human row draws the account's icon and a bot row the ship it flies; the two are different things, and an account that never captured one draws nothing. */
  function portraitOf(slot: LobbySlot): SeatShip | null {
    const player = occupantPlayer(slot);
    if (player === null) return slot.ship;
    if (player.profileShip === null) return null;
    return {
      shipId: player.profileShip.shipId,
      skinProductId: player.profileShip.skinProductId,
    };
  }

  function skinCss(ship: SeatShip, team: number) {
    void atlasSignal.generation;
    const skin = wornSkin(socketState.products, ship.skinProductId, team);
    return skin === null
      ? null
      : sharedAssets.cosmeticCss(skin.imagePath, skin.shift, LOBBY_UI.portrait);
  }

  function chooseDifficulty(team: number, event: Event): void {
    const select = event.currentTarget;
    if (!(select instanceof HTMLSelectElement)) return;
    const tier = PLACEHOLDER_DIFFICULTY_TIERS.find((entry) => entry === select.value);
    if (tier !== undefined) ondifficulty(team, tier);
  }
</script>

{#if !lobby.started}
  <div class="lobby" data-testid="lobby-screen">
    <div class="lobby__panel">
      <MetalFrame />

      <div class="lobby__content">
        <h2 class="lobby__heading">{lobby.name}</h2>

        <p class="lobby__meta">
          <span>Host: {lobby.hostName}</span>
          <span>{lobby.visibility === 'private' ? 'Private' : 'Public'}</span>
          <span>{taken}/{lobby.slots.length} pilots</span>
          {#if watching}<span>Spectating</span>{/if}
        </p>

        <p class="lobby__code" data-testid="lobby-join-code">
          <span>Join code</span>
          <b>{lobby.joinCode}</b>
          <button
            class="wis-btn-inline lobby__copy"
            type="button"
            data-testid="lobby-copy-link"
            onclick={copyLink}>{copied ? 'Copied' : 'Copy link'}</button
          >
        </p>

        <div class="lobby__body">
          {#each lobby.teams as entry (entry.team)}
            {@const team = entry.team}
            <section class="lobby__well lobby__team">
              <header class="lobby__team-head">
                <h3 class="lobby__team-name" style:color={LOBBY_UI.teamInk[team]}>
                  {LOBBY_UI.teamLabels[team]}
                </h3>
                <select
                  class="wis-select wis-select--wide lobby__difficulty"
                  data-testid="lobby-difficulty-{team}"
                  aria-label="{LOBBY_UI.teamLabels[team]} bot difficulty"
                  disabled={!mayTune}
                  value={difficultyOf(lobby, team)}
                  onchange={(event) => {
                    chooseDifficulty(team, event);
                  }}
                >
                  {#each PLACEHOLDER_DIFFICULTY_TIERS as tier (tier)}
                    <option value={tier}>{tier}</option>
                  {/each}
                </select>
              </header>

              <ul class="lobby__slots">
                {#each teamSlots(lobby, team) as slot (slot.slotId)}
                  {@const player = occupantPlayer(slot)}
                  {@const portrait = portraitOf(slot)}
                  {@const skin = portrait === null ? null : skinCss(portrait, slot.team)}
                  <li
                    class="lobby__slot"
                    class:lobby__slot--mine={player !== null && player.participantId === selfId}
                    data-testid="lobby-slot"
                  >
                    <span
                      class="lobby__art"
                      class:lobby__art--bot={player === null}
                      class:lobby__art--empty={portrait === null}
                      data-testid="lobby-portrait"
                    >
                      {#if portrait !== null}
                        <ShipArt
                          class="lobby__layer"
                          team={slot.team}
                          shipId={portrait.shipId}
                          size={LOBBY_UI.portrait}
                        />
                      {/if}
                      {#if skin !== null}
                        <span
                          class="lobby__layer"
                          style:background-image="url({skin.url})"
                          style:background-size={skin.backgroundSize}
                          style:background-position={skin.backgroundPosition}
                          style:width="{skin.width}px"
                          style:height="{skin.height}px"
                        ></span>
                      {/if}
                    </span>

                    {#if player === null}
                      <span class="lobby__name lobby__name--bot">Bot</span>
                      {#if canTakeSlot(lobby, slot.slotId, selfId)}
                        <button
                          class="wis-btn-inline lobby__take"
                          type="button"
                          data-testid="lobby-take"
                          onclick={() => {
                            ontake(slot.slotId);
                          }}>Take</button
                        >
                      {/if}
                    {:else}
                      <span class="lobby__name"
                        >{player.name === '' ? UNNAMED_NAME : player.name}</span
                      >
                      {#if isHost(lobby, player.participantId)}
                        <span class="lobby__guest">host</span>
                      {/if}
                      {#if player.guest}<span class="lobby__guest">guest</span>{/if}
                    {/if}
                  </li>
                {/each}
              </ul>
            </section>
          {/each}

          <section class="lobby__well lobby__spectators">
            <header class="lobby__team-head">
              <h3 class="lobby__team-name">Spectators</h3>
              {#if mayWatch}
                <button
                  class="wis-btn-inline lobby__take"
                  type="button"
                  data-testid="lobby-spectate"
                  onclick={onspectate}>Watch</button
                >
              {:else}
                <span class="lobby__count">{lobby.spectators.length}</span>
              {/if}
            </header>
            <ul class="lobby__slots lobby__slots--scroll" data-testid="lobby-spectators">
              {#each lobby.spectators as watcher (watcher.participantId)}
                <li
                  class="lobby__slot lobby__slot--watcher"
                  class:lobby__slot--mine={watcher.participantId === selfId}
                >
                  <span class="lobby__name"
                    >{watcher.name === '' ? UNNAMED_NAME : watcher.name}</span
                  >
                  {#if watcher.participantId === selfId}<span class="lobby__guest">you</span>{/if}
                </li>
              {:else}
                <li class="lobby__slot lobby__slot--watcher">
                  <span class="lobby__name lobby__name--bot">Nobody watching</span>
                </li>
              {/each}
            </ul>
            <p class="lobby__hint">
              {watching ? 'Take any bot seat to play' : 'Watch to give up your seat'}
            </p>
          </section>
        </div>

        <div class="lobby__well lobby__chat">
          <LobbyChat lines={chat} onsend={onchat} />
        </div>

        <div class="lobby__footer">
          {#if canCancelStart(lobby, selfId)}
            <button
              class="wis-btn-play lobby__start"
              type="button"
              data-testid="lobby-cancel-start"
              onclick={oncancelstart}>Cancel ({startSeconds(lobby)})</button
            >
          {:else if canStart(lobby, selfId)}
            <button class="wis-btn-play lobby__start" type="button" onclick={onstart}>Start</button>
          {:else if lobby.startInMs > 0}
            <span class="lobby__waiting" data-testid="lobby-starting"
              >Starting in {startSeconds(lobby)}</span
            >
          {:else}
            <span class="lobby__waiting">Waiting for {lobby.hostName} to start</span>
          {/if}

          <button class="wis-btn lobby__action" type="button" onclick={onleave}>Leave</button>
        </div>
      </div>
    </div>
  </div>
{/if}

<style>
  .lobby {
    position: fixed;
    inset: 0;
    z-index: 5;
    display: grid;
    place-items: center;
    font-family: var(--wis-font);
    pointer-events: auto;
  }

  .lobby__hint {
    margin: 8px 0 0;
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 1px;
    color: #97a0b6;
  }

  .lobby__panel {
    position: relative;
    width: min(940px, 96vw);
    height: min(680px, 94vh);
    color: var(--wis-ink);
  }

  .lobby__content {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    padding: 22px 44px 26px;
  }

  .lobby__heading {
    margin: 0 0 10px;
    text-align: center;
    text-transform: uppercase;
    letter-spacing: 3px;
    font-size: 20px;
    font-weight: normal;
  }

  .lobby__meta,
  .lobby__code {
    margin: 0;
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 4px 22px;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 1px;
  }

  .lobby__meta span {
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .lobby__code {
    margin-top: 6px;
    gap: 4px 10px;
    color: #303030;
  }

  .lobby__copy {
    font-size: 11px;
    letter-spacing: 1px;
  }

  .lobby__code b {
    letter-spacing: 4px;
    color: var(--wis-ink);
  }

  .lobby__body {
    flex: 1 1 auto;
    min-height: 0;
    margin-top: 12px;
    display: flex;
    gap: 14px;
  }

  .lobby__well {
    display: flex;
    min-width: 0;
    flex-direction: column;
    padding: 10px 12px 12px;
    background:
      repeating-linear-gradient(45deg, rgb(255 255 255 / 5%) 0 2px, transparent 2px 4px),
      repeating-linear-gradient(-45deg, rgb(0 0 0 / 38%) 0 2px, transparent 2px 4px),
      linear-gradient(#242423, #171716);
    box-shadow:
      inset 0 2px 5px rgb(0 0 0 / 85%),
      inset 0 -1px 0 rgb(255 255 255 / 14%),
      0 1px 0 rgb(255 255 255 / 22%);
  }

  .lobby__team {
    flex: 1 1 0;
  }

  .lobby__spectators {
    flex: 0 1 200px;
  }

  .lobby__chat {
    flex: 0 0 168px;
    margin-top: 12px;
  }

  .lobby__spectators .lobby__team-name {
    font-size: 13px;
  }

  .lobby__team-head {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 6px 8px;
    margin-bottom: 8px;
  }

  .lobby__team-name {
    flex: 1 1 auto;
    margin: 0;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 15px;
    font-weight: normal;
    text-transform: uppercase;
    letter-spacing: 2px;
    color: #d7deef;
    text-shadow: 0 2px 3px rgb(0 0 0 / 80%);
  }

  .lobby__count {
    font-size: 13px;
    color: #d7deef;
  }

  .lobby__difficulty {
    flex: 0 0 auto;
    width: 150px;
    background-position:
      132px center,
      0 0;
    background-size:
      9px 6px,
      150px 30px;
  }

  .lobby__slots {
    container: lobby-slots / inline-size;
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    scrollbar-width: thin;
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin: 0;
    padding: 0;
    list-style: none;
  }

  .lobby__slots--scroll {
    overflow-y: auto;
    scrollbar-width: thin;
  }

  .lobby__slot {
    display: flex;
    min-width: 0;
    align-items: center;
    gap: 10px;
    height: 44px;
    padding: 0 10px;
    color: #e6ebf7;
    font-size: 13px;
    background: linear-gradient(
      rgb(255 255 255 / 18%),
      rgb(255 255 255 / 3%) 45%,
      rgb(0 0 0 / 18%)
    );
    box-shadow:
      inset 0 1px 0 rgb(255 255 255 / 40%),
      inset 0 -1px 0 rgb(0 0 0 / 55%);
  }

  .lobby__slot--mine {
    box-shadow:
      inset 0 0 0 1px #d8c07a,
      inset 0 1px 0 rgb(255 255 255 / 40%);
  }

  .lobby__slot--watcher {
    height: 30px;
  }

  .lobby__art {
    position: relative;
    flex: 0 0 auto;
    width: 34px;
    height: 34px;
  }

  /* Below this the row cannot hold both, and a name clipped to four characters names nobody. */
  @container lobby-slots (width < 190px) {
    .lobby__art {
      display: none;
    }
  }

  .lobby__art--bot {
    opacity: 0.42;
    filter: grayscale(0.7);
  }

  .lobby__art--empty {
    box-shadow: inset 0 0 0 1px rgb(230 235 247 / 26%);
    opacity: 1;
    filter: none;
  }

  .lobby__art :global(.lobby__layer) {
    position: absolute;
    top: 0;
    left: 0;
    background-repeat: no-repeat;
  }

  .lobby__name {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .lobby__name--bot {
    color: #97a0b6;
  }

  .lobby__take {
    flex: 0 0 auto;
    padding: 7px 12px;
  }

  .lobby__guest {
    flex: 0 0 auto;
    font-size: 10px;
    color: #97a0b6;
    text-transform: uppercase;
  }

  .lobby__footer {
    flex: 0 0 auto;
    margin-top: 14px;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    gap: 10px 16px;
  }

  .lobby__start {
    padding: 14px 40px;
    font-size: 15px;
  }

  .lobby__action {
    padding: 12px 26px;
  }

  .lobby__waiting {
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 1px;
  }
</style>
